import { useCallback, useState } from "react";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { useI18n } from "../../i18n/index.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";

export function MaterialDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  onCreated(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("each");
  const [standardCost, setStandardCost] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await api.createMaterial({ sku, name, unitOfMeasure: unit, standardCost });
      push(t("catalog.material.addedToast"), name);
      setSku("");
      setName("");
      setStandardCost("");
      onOpenChange(false);
      onCreated();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [name, onCreated, onOpenChange, push, sku, standardCost, unit, t]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("catalog.material.addTitle")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={busy || !sku.trim() || !name.trim()}
          >
            {busy ? t("common.saving") : t("catalog.material.saved")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <TextInput
          label={t("common.fields.sku")}
          value={sku}
          onChange={(e) => setSku(e.target.value)}
        />
        <TextInput
          label={t("common.fields.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextInput
          label={t("common.fields.unit")}
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
        />
        <TextInput
          label={t("catalog.column.standardCost")}
          value={standardCost}
          hint={t("catalog.material.costHint")}
          onChange={(e) => setStandardCost(e.target.value)}
        />
      </div>
    </Modal>
  );
}
